//@ts-strict-ignore
import React, { ReactElement, useContext, useMemo, useState } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client';
import DropdownEditor from '../../components/DropdownEditor';
import BinLocationForProductAndWarehouse from '../../graphql/queries/product/BinLocationForProductAndWarehouse.graphql';
import StockLotsWithFreeStockByBin from '../../graphql/queries/product/StockLotsWithFreeStockByBin.graphql';
import { StockLotOptionRenderer } from '../../graphql/entities/product/StockLot';
import ProductComponentAllComponents from 'graphql/queries/product/ProductComponentAllComponents.graphql';
import { MenuItem } from 'react-contextmenu';
import { ICommit, IEditableDataGridColumnDef } from '../../@types/editableDataGridTypes';
import {
  IAllocationSalesHelperStock,
  IBinLocationForProductAndWarehouseQueryResult,
  IBinLocationForProductAndWarehouseQueryVariables,
  IProduct,
  IProductComponentAllComponentsQueryResult,
  IProductComponentAllComponentsQueryVariables,
  IStockLot,
  IStockLotsWithFreeStockByBinQueryVariables,
  IWarehouse,
  Maybe,
  ProductClass,
} from '../../graphql/schema/p4types';
import EditableDataGrid from '../EditableDataGrid/EditableDataGrid';
import { useDispatch } from 'react-redux';
import { useRefFunction } from '../../utils/hooks';
import getQuantityDP from '../../utils/getProductDP';
import ConnectedFormContext from '../Context/ConnectedFormContext';

export type IAllocationWindowProps = {
  editable: boolean;
  onChange?: (updatedValue: any) => void;
  onDuplicate: (rowData: any, rowIdx?: number) => void;
  product: Maybe<IProduct>;
  value: Maybe<Array<IAllocationSalesHelperStock>>;
  warehouse: Maybe<IWarehouse>;
  rowIdx: number; //Row index of the parent row (The allocationSalesHelper rowIdx).
};

type IBinLocationOptions = Array<
  Maybe<{
    __typename?: 'BinLocation';
    id?: Maybe<any>;
    uuid?: Maybe<string>;
    concurrencyVersion?: Maybe<any>;
    location?: Maybe<string>;
  }>
>;

const AllocationWindow = ({
  editable,
  onChange,
  onDuplicate: onDuplicateFromProps,
  product,
  value,
  warehouse,
  rowIdx,
}: IAllocationWindowProps) => {
  const [lotData, setLotData] = useState<Array<IStockLot>>([]);
  const dispatch = useDispatch();
  const { change } = useContext(ConnectedFormContext);

  const { data: rawComponentData } = useQuery<
    IProductComponentAllComponentsQueryResult,
    IProductComponentAllComponentsQueryVariables
  >(ProductComponentAllComponents, {
    fetchPolicy: 'network-only',
    variables: {
      productId: product?.id,
    },
  });

  const componentData = rawComponentData?.data || [];

  const { data: rawBinLocationData, refetch: refetchBinLocationData } = useQuery<
    IBinLocationForProductAndWarehouseQueryResult,
    IBinLocationForProductAndWarehouseQueryVariables
  >(BinLocationForProductAndWarehouse, {
    fetchPolicy: 'network-only',
    variables: {
      productId: product?.id,
      warehouseId: warehouse?.id,
      binLocationCriteria: {
        hasFreeStock: true,
        quarantineBinLocationsHidden: true,
      },
    },
  });
  const [fetchStockLotsWithFreeStockByBin] = useLazyQuery<
    { list: IStockLot[] },
    IStockLotsWithFreeStockByBinQueryVariables
  >(StockLotsWithFreeStockByBin, {
    fetchPolicy: 'network-only',
  });

  const fetchLots = (productId: string, binId: string) => {
    const variables = {
      productId: productId,
      binLocationId: binId,
    };
    fetchStockLotsWithFreeStockByBin({
      variables,
    }).then((response) => {
      setLotData(response.data?.list || []);
    });
  };

  const clearDeallocateAllForLine = useRefFunction(() => {
    dispatch(change(`allocationSalesHelper.${rowIdx}.deallocateAll`, false));
  });

  const clearUnreserveAllForLine = useRefFunction(() => {
    dispatch(change(`allocationSalesHelper.${rowIdx}.unReserveAll`, false));
  });

  const rowUpdated = (updatedValue: ICommit<IAllocationSalesHelperStock>) => {
    if (!updatedValue.updated) {
      throw new Error('Expected an updated value.');
    }

    if (updatedValue.cellKey === 'product' && updatedValue.updated.product) {
      updatedValue.cellKeys = ['binLocation', 'componentQuantity'];
      updatedValue.updated.componentQuantity = componentData?.find((component) => {
        return component?.product?.id === updatedValue.updated!.product!.id;
      })?.quantity;
    }

    onChange?.(updatedValue);

    if (updatedValue.cellKey === 'allocation') {
      clearDeallocateAllForLine();
    } else if (updatedValue.cellKey === 'reservation') {
      clearUnreserveAllForLine();
    }
  };

  const onDuplicate = (rowData: any, rowIdx?: number) => {
    onDuplicateFromProps(rowData, rowIdx);
  };

  const renderActionButtons = ({ rowData }) => {
    const renderedButtons: Array<ReactElement> = [];

    renderedButtons.push(
      <MenuItem
        key="duplicateBtn"
        onClick={(e) => {
          e.preventDefault();
          onDuplicate(rowData);
        }}
      >
        Duplicate
      </MenuItem>
    );

    return renderedButtons;
  };

  //Cannot assume that the options function provided to the cell is up-to-date, wrap with useRefFunction to ensure we have the latest data.
  const binLocationOptions = useRefFunction<() => IBinLocationOptions>(
    () => rawBinLocationData?.list?.map((binLocationStock) => binLocationStock?.binLocation) || []
  );
  const lotOptions = useRefFunction<() => Array<IStockLot>>(() => lotData);

  //Cannot assume that the options function provided to the cell is up-to-date, wrap with useRefFunction to ensure we have the latest data.
  const componentOptions = useRefFunction<() => Array<IProduct>>(() =>
    componentData.map((pcq) => pcq?.product as IProduct)
  );

  const referenceColumnMeta: Array<IEditableDataGridColumnDef> = useMemo(() => {
    //There's no rowData to get the product from in the breakdown grid, hence this wrapper for getting the DP.
    const getQuantityDPBreakdown = (rowData) => {
      return getQuantityDP(rowData?.product ? rowData : { product });
    };

    const columnMeta = [
      {
        name: 'Bin Location',
        key: 'binLocation',
        editable: (rowData) => {
          return !rowData || !rowData.allocated || parseFloat(rowData.allocated) === 0.0 || !rowData.stockLot;
        },
        editor: <DropdownEditor rawLabel={true} />,
        options: {
          dropdown: {
            valueKey: 'id',
            labelKey: 'location',
            options: binLocationOptions,
          },
        },
        formatter: 'dropdown',
      },
      {
        name: 'Lot Attributes',
        key: 'stockLot',
        editable: (rowData) => {
          return !rowData || !rowData.allocated || parseFloat(rowData.allocated) === 0.0 || !rowData.stockLot;
        },
        editor: <DropdownEditor />,
        options: {
          dropdown: {
            valueKey: 'id',
            labelKey: 'lotAttributeValues[0].value',
            options: lotOptions,
            optionRenderer: StockLotOptionRenderer,
            valueRenderer: StockLotOptionRenderer,
          },
        },
        formatter: 'dropdown',
      },
      {
        name: 'Allocated',
        key: 'allocated',
        formatter: 'numeric',
        options: {
          format: { decimals: getQuantityDPBreakdown },
        },
        editable: false,
      },
      {
        name: 'To Allocate',
        key: 'allocation',
        formatter: 'numeric',
        options: {
          format: { decimals: getQuantityDPBreakdown },
        },
      },
    ];

    if (product?.productClass === ProductClass.Kit) {
      columnMeta.unshift(
        {
          name: 'Index',
          key: 'levelIndex',
          width: 100,
          editable: false,
        },
        {
          name: 'Product',
          key: 'product',
          editor: <DropdownEditor />,
          options: {
            dropdown: {
              valueKey: 'id',
              labelKey: 'reference',
              options: componentOptions,
            },
          },
          editable: (rowData) => {
            return !rowData.product;
          },
          formatter: 'dropdown',
        },
        {
          name: 'Total Required',
          key: 'componentQuantity',
          formatter: 'numeric',
          editable: false,
        },
        {
          name: 'Free Stock',
          key: 'freeStock',
          formatter: 'numeric',
          editable: false,
        }
      );

      columnMeta.splice(
        columnMeta.length - 2,
        0,
        {
          name: 'Reserved',
          key: 'reserved',
          formatter: 'numeric',
          editable: false,
          options: {
            format: { decimals: getQuantityDP },
          },
        },
        {
          name: 'To Reserve',
          key: 'reservation',
          formatter: 'numeric',
          options: {
            format: { decimals: getQuantityDP },
          },
        }
      );
    }

    return columnMeta;
  }, [binLocationOptions, componentOptions, lotOptions, product]);

  const isKit = product?.productClass === ProductClass.Kit;

  let rows = 1;
  if (value) {
    rows = value.length + (!isKit ? 1 : 0);
  }

  return (
    <EditableDataGrid
      addBlankRow={!isKit}
      enableCellSelect={editable}
      listData={value as Array<IAllocationSalesHelperStock>}
      columns={referenceColumnMeta}
      rowsCount={rows}
      withRowUpdated={rowUpdated}
      enableContextMenu={true}
      renderActionButtons={renderActionButtons}
      onCellSelected={(_dataid: string, _selected, rowData: IAllocationSalesHelperStock) => {
        refetchBinLocationData({
          productId: isKit ? rowData.product?.id : product?.id,
          warehouseId: warehouse?.id,
          binLocationCriteria: {
            hasFreeStock: true,
            quarantineBinLocationsHidden: true,
          },
        }).then(() => {
          if (isKit ? rowData.product?.id : product?.id && rowData.binLocation?.id) {
            fetchLots(isKit ? rowData.product?.id : product?.id, rowData.binLocation?.id);
          } else {
            setLotData([]);
          }
        });
      }}
      contextKey="breakdown"
    />
  );
};

export default AllocationWindow;
